import moment from 'moment/moment'

import { reportingColors } from '../../../utils/colors'
import * as FormatUtils from '../../../utils/numbers'
import {
    LabelAnchorType,
    PSVisualizationDataType,
    Rect,
    displayLabel,
    drawRoundedRect,
} from '../../../utils/visualization'
import { Color, parseColor } from '../../../utils/visualization'
import {
    DEFAULT_CAPTION_FONT_SIZE,
    DEFAULT_LABEL_FONT_SIZE,
    DEFAULT_LIGHT_FONT,
} from '../../../utils/visualization'
import { HeatMapElement } from './HeatMapElement'

const ELEMENT_HEIGHT = 24
const TOP_CAPTION_HEIGHT = 40
const TOP_CAPTION_PADDING_HORIZ = 15
const TOP_CAPTION_PADDING_VERT = 10
const TOP_CAPTION_MARGIN = 4
const TOP_LABEL_MARGIN = 8
const RIGHT_LABEL_MARGIN = 12
const GRID_GAP = 4

export const generateHeatMapRenderer = () => {
    const labelFontName = DEFAULT_LIGHT_FONT
    const labelFontSize = DEFAULT_LABEL_FONT_SIZE
    const captionFontSize = DEFAULT_CAPTION_FONT_SIZE

    const elementRepository: HeatMapElement[] = []
    let values: { [key: string]: number }[] = []
    let labels: moment.Moment[] = []

    var selectedElementIndex: number | null = null
    var dataType: PSVisualizationDataType = PSVisualizationDataType.CURRENCY
    var elementColor: Color = parseColor(reportingColors.blue400)
    var nCol: number = 0
    var nRow: number = 0
    var elementWidth: number = 0
    var dpr: number = 1

    var mapRect: Rect
    var captionRect: Rect

    const drawValues = (
        canvas: HTMLCanvasElement,
        valuesArg: { [key: string]: number }[],
        labelsArg: moment.Moment[],
        colorArg: string,
        dataTypeArg: PSVisualizationDataType,
        selectedElementIndexArg: number | null,
        dprArg: number
    ) => {
        labels = labelsArg
        values = valuesArg
        elementColor = parseColor(colorArg)
        dataType = dataTypeArg
        selectedElementIndex = selectedElementIndexArg
        dpr = dprArg
        draw(canvas)
    }

    const draw = (canvas: HTMLCanvasElement) => {
        const ctx = canvas.getContext('2d')
        if (ctx === null) {
            return
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height)

        const labelFont = `${labelFontSize}px ${labelFontName}`
        let rightLabel = '00:00 AM'
        let topLabel = 'Mmm'
        ctx.font = labelFont
        const rightLabelSize = ctx.measureText(rightLabel)
        const topLabelSize = ctx.measureText(topLabel)
        const topLabelHeight =
            topLabelSize.actualBoundingBoxAscent + topLabelSize.actualBoundingBoxDescent

        const ww = canvas.width / dpr
        const h1 = TOP_CAPTION_HEIGHT + 2 * TOP_CAPTION_MARGIN
        const h2 = h1 + TOP_CAPTION_MARGIN
        const h3 = h2 + topLabelHeight
        const h4 = h3 + TOP_LABEL_MARGIN

        const w2 = ww - rightLabelSize.width
        const w1 = w2 - RIGHT_LABEL_MARGIN

        let colName = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
        let colMap: { [key: string]: number } = {
            sun: 0,
            mon: 1,
            tue: 2,
            wed: 3,
            thu: 4,
            fri: 5,
            sat: 6,
        }

        let labelArray: string[] = []
        let valueArray: { [key: string]: number }[] = []

        const timeOnlyDateFormatter = 'HH:mm'
        const timeAMPMDateFormatter = 'h:mm A'

        for (let ii = 0; ii < labels.length; ii++) {
            let date = moment(labels[ii], timeOnlyDateFormatter)
            labelArray.push(date.format(timeAMPMDateFormatter))
            valueArray[ii] = values[ii]
        }

        if (labelArray.length === 0) {
            const hStart = 8
            const hEnd = 19

            for (let h = hStart; h <= hEnd; h++) {
                let timeString = `${h}:00`
                let timeMoment = moment(timeString, 'HH:mm')
                labelArray.push(timeMoment.format(timeOnlyDateFormatter))
                valueArray.push({})
            }
        }

        nCol = Object.keys(colMap).length
        nRow = labelArray.length

        canvas.style.height = h4 + nRow * (ELEMENT_HEIGHT + GRID_GAP) + 'px'
        canvas.height = (h4 + nRow * (ELEMENT_HEIGHT + GRID_GAP)) * dpr
        ctx.scale(dpr, dpr)

        let h5 = h4 + (nRow - 1) * (ELEMENT_HEIGHT + GRID_GAP) + ELEMENT_HEIGHT
        const xOffset = GRID_GAP
        mapRect = { x: xOffset, y: h4, width: w1, height: h5 }
        captionRect = { x: xOffset, y: TOP_LABEL_MARGIN, width: w1, height: h1 }
        elementWidth = (w1 - (nCol - 1) * GRID_GAP - mapRect.x) / nCol

        const availableelementCount = elementRepository.length

        let idx = 0
        for (let ii = 0; ii < nRow; ii++) {
            let x = mapRect.x
            let y = mapRect.y + ii * (ELEMENT_HEIGHT + GRID_GAP)
            for (let jj = 0; jj < nCol; jj++) {
                let element = null
                if (idx >= availableelementCount) {
                    element = new HeatMapElement({
                        x: x,
                        y: y,
                        width: elementWidth,
                        height: ELEMENT_HEIGHT,
                    })
                    elementRepository.push(element)
                }
                else {
                    element = elementRepository[idx]
                }
                x += elementWidth + GRID_GAP
                idx += 1
            }
        }

        let x = elementWidth / 2
        let y = h2 + topLabelSize.actualBoundingBoxAscent + topLabelSize.actualBoundingBoxDescent
        ctx.font = labelFont
        for (let dow of colName) {
            let dowString = dow.charAt(0).toUpperCase() + dow.slice(1)
            displayLabel(ctx, dowString, { x: x, y: y }, LabelAnchorType.BOTTOM)
            x += elementWidth + GRID_GAP
        }

        x = ww - rightLabelSize.width
        y = h4 + ELEMENT_HEIGHT / 2
        for (let label of labelArray) {
            displayLabel(ctx, label, { x: x, y: y }, LabelAnchorType.LEFT)
            y += ELEMENT_HEIGHT + GRID_GAP
        }

        let maxValue = 0
        for (let value of valueArray) {
            for (let dow in value) {
                maxValue = Math.max(maxValue, value[dow])
            }
        }

        if (dataType == PSVisualizationDataType.PERCENT) {
            maxValue = Math.max(maxValue, 100)
        }

        if (maxValue == 0) {
            maxValue = 1
        }

        for (let ii = 0; ii < valueArray.length; ii++) {
            let valueDict = valueArray[ii]
            let time = moment(labelArray[ii], timeAMPMDateFormatter)
            for (let dow in colMap) {
                let colIndex = colMap[dow]
                let dayOfWeek = moment(time)
                dayOfWeek.add((colIndex % 7) - dayOfWeek.day(), 'days')
                let value = valueDict[dow] || 0
                let index = ii * nCol + colIndex
                const selected = selectedElementIndex === index
                elementRepository[index].updateValue(
                    value,
                    maxValue,
                    dayOfWeek.toDate(),
                    elementColor,
                    selected
                )
                elementRepository[index].draw(ctx)
                if (selected) {
                    displayElementValueAndDates(ctx, elementRepository[index])
                }
            }
        }
    }

    const captionStringForDate = (date: Date) => {
        let prefix = 'on '
        let formatter = 'ddd h:mmA'
        return prefix + moment(date).format(formatter)
    }

    const displayElementValueAndDates = (
        ctx: CanvasRenderingContext2D,
        element: HeatMapElement
    ) => {
        const fontStyle = `${captionFontSize}px ${labelFontName}`
        const fontStyleBold = `bold ${fontStyle}`

        let formattedValue = FormatUtils.currencyFormatReporting(element.value)
        if (dataType === PSVisualizationDataType.INTEGER) {
            formattedValue = Math.round(element.value).toString()
        }
        else if (dataType === PSVisualizationDataType.PERCENT) {
            formattedValue = `${Math.round(element.value)}%`
        }

        const valueString = formattedValue
        ctx.font = fontStyleBold
        const valueStringSize = ctx.measureText(valueString)

        const datesString = ' ' + captionStringForDate(element.label!)
        ctx.font = fontStyle
        const datesStringSize = ctx.measureText(datesString)

        const textWidth = valueStringSize.width + datesStringSize.width
        const valueTextHeight =
            valueStringSize.actualBoundingBoxAscent + valueStringSize.actualBoundingBoxDescent
        const datesTextHeight =
            datesStringSize.actualBoundingBoxAscent + datesStringSize.actualBoundingBoxDescent
        const textHeight = Math.max(valueTextHeight, datesTextHeight)

        let elementCenterX = element.mapRect.x + element.mapRect.width / 2
        let captionWidth = textWidth + 2 * TOP_CAPTION_PADDING_HORIZ
        let captionHeight = textHeight + 2 * TOP_CAPTION_PADDING_VERT
        let captionLeft = elementCenterX - captionWidth / 2

        if (captionLeft + captionWidth > mapRect.width) {
            captionLeft = mapRect.width - captionWidth
        }
        else if (captionLeft < mapRect.x) {
            captionLeft = mapRect.x
        }

        drawRoundedRect(
            ctx,
            captionLeft,
            captionRect.y,
            captionWidth,
            captionHeight,
            TOP_CAPTION_PADDING_HORIZ,
            reportingColors.gray200
        )
        ctx.font = fontStyleBold
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'
        const maxWidth = 150
        let stringX = captionLeft + TOP_CAPTION_PADDING_HORIZ
        let stringY = captionRect.y + TOP_CAPTION_PADDING_VERT
        ctx.fillStyle = reportingColors.gray700
        ctx.fillText(valueString, stringX, stringY, maxWidth)
        ctx.font = fontStyle
        stringX += valueStringSize.width
        ctx.fillStyle = reportingColors.gray600
        ctx.fillText(datesString, stringX, stringY, maxWidth)
    }

    return { drawValues, elementRepository }
}
